import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Close, ArrowForward, ArrowBack } from '@mui/icons-material';
import { NextBehavior } from '@uplift/types';
import { Button } from '@uplift/ui-v2';
import clsx from 'clsx';
import React from 'react';
import { useFooterStyles } from './form-layout-styles';

export type FormFooterProps = {
  buttonText: string;
  nextBehavior: NextBehavior;
  showBack: boolean;
  showNext: boolean;
  isFinal: boolean;
  disabledRight: boolean;
  onBack: () => void;
  onCancel: () => void;
  onClick: () => void;
};

export const FormFooter: React.VFC<FormFooterProps> = ({ buttonText, nextBehavior, showBack, showNext, isFinal, disabledRight, onBack, onCancel, onClick }) => {
  const { classes } = useFooterStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const isEditing = nextBehavior === NextBehavior.SkipToLast;
  const leftAlignedButton = !isEditing && showBack && !showNext;
  const spaceBetweenButtons = showBack && showNext;
  const centerButton =
    (isEditing && showBack && !showNext) // editing a single select
    || (!showBack && showNext); // review page
  const showTopBorder = spaceBetweenButtons || (isEditing && showBack && !showNext);

  const leftButton = React.useMemo(() => {
    if (isEditing && showBack) {
      return (
        <Button
          type="button"
          variant={isDesktop ? 'text' : 'outlined'}
          startIcon={<Close />}
          onClick={onCancel}
        >
          Cancel
        </Button>
      );
    }

    if (isDesktop && showBack) {
      return (
        <Button type="button" color="primary" startIcon={<ArrowBack />} onClick={onBack}>
          Back
        </Button>
      );
    }

    return null;
  }, [ isDesktop, isEditing, onBack, onCancel, showBack ]);

  const rightButton = React.useMemo(() => {
    if (!showNext) {
      return null;
    }

    if (isEditing && !isFinal) {
      return (
        <Button
          type="button"
          variant="contained"
          endIcon={<ArrowForward />}
          color="secondary"
          onClick={onClick}
          disabled={disabledRight}
        >
          Update
        </Button>
      );
    }

    if (!buttonText) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="contained"
        color="secondary"
        onClick={onClick}
        endIcon={<ArrowForward />}
        fullWidth
        disabled={disabledRight}
      >
        {buttonText}
      </Button>
    );
  }, [ buttonText, disabledRight, isEditing, isFinal, onClick, showNext ]);

  return (
    <Box className={clsx(classes.footer, showTopBorder ? classes.footerBorder : classes.noBorder)}>
      <Box
        className={clsx(
          classes.footerBase,
          leftAlignedButton && classes.alignButtonLeft,
          spaceBetweenButtons && classes.spaceBetween,
          centerButton && classes.centerButton,
        )}
      >
        {leftButton}
        {rightButton}
      </Box>
    </Box>
  );
};
